import { data, externalData, type ObservableValue } from '@amazon/vinyl-observable'
import type { Territory } from '@amzn/vinyl-amzn'
import { ctx } from './util/context'
import { globalRef, logDebug, logWarn, noop } from '@amazon/vinyl-util'
import { getAuth } from '@/auth/authRef'
import { recordResolvedTerritory } from '@/resolvedTerritory'
import { AuthStatus, createAuthScopedMemo, type UserContextResult } from '@amzn/bf-playback'
import { loadUserContext, readCachedUserContext } from '@/auth/userContext'
import {
    hostTerritory,
    MARKETPLACES,
    toMusicTerritory,
    type RetailTerritory,
    retailTerritory,
} from '@/territory'

const { target, errorHandler } = ctx('marketplace')

/**
 * The customer's marketplace, expressed as the music territory country code
 * and the retail marketplace id.
 */
export type MarketplaceInfo = {
    readonly musicTerritory: Territory
    readonly marketplaceId: string
}

// DMLS authorizes against the territory/marketplace pair, so the id follows the retail site.
const RETAIL_MARKETPLACE_ID = Object.fromEntries(
    MARKETPLACES.map((m) => [m.territory, m.marketplaceId])
) as Record<RetailTerritory, string>

function marketplaceFor(musicTerritory: Territory): MarketplaceInfo {
    return { musicTerritory, marketplaceId: RETAIL_MARKETPLACE_ID[retailTerritory(musicTerritory)] }
}

function territoryOf(context: UserContextResult): Territory | undefined {
    return toMusicTerritory(context.user?.market?.id)
}

/** The signed-in account's territory, undefined when it has none or the lookup fails. */
export async function accountTerritory(): Promise<Territory | undefined> {
    try {
        return territoryOf(await loadUserContext())
    } catch (err) {
        logWarn(target, 'account territory unavailable, using the page host', err)
        return undefined
    }
}

/** The cached user context, else the network read's; the network read's whenever it lands first. */
async function firstUserContext(network: Promise<UserContextResult>): Promise<UserContextResult> {
    const cached = readCachedUserContext().catch(() => undefined)
    try {
        return await Promise.race([network, cached.then((hit) => hit ?? network)])
    } catch (err) {
        const hit = await cached
        if (hit) return hit
        throw err
    }
}

type Resolution = { readonly under: AuthStatus; readonly marketplace: MarketplaceInfo }

const marketplaceRef = globalRef(() => {
    let resolved: Resolution | undefined
    const accountTerritory$ = data<Territory | null>(null)
    const settle = (under: AuthStatus, territory: Territory | undefined): MarketplaceInfo => {
        const marketplace = marketplaceFor(territory ?? hostTerritory())
        logDebug(target, `marketplace resolved to ${marketplace.musicTerritory}`)
        resolved = { under, marketplace }
        recordResolvedTerritory(marketplace.musicTerritory)
        accountTerritory$.value = under === AuthStatus.SIGNED_IN ? (territory ?? null) : null
        return marketplace
    }
    const follow = (under: AuthStatus, fresh: UserContextResult): void => {
        const territory = territoryOf(fresh)
        if ((territory ?? null) === accountTerritory$.value || getAuth().status.value !== under) {
            return
        }
        settle(under, territory)
        memo.reset()
    }
    // GH's getCurrentMarket(): the account's territory once known, else the page host.
    const memo = createAuthScopedMemo(getAuth, async () => {
        const under = getAuth().status.value
        if (under !== AuthStatus.SIGNED_IN) return settle(under, undefined)
        const network = loadUserContext()
        try {
            const territory = territoryOf(await firstUserContext(network))
            // The cached market stands when the network read fails.
            network.then((fresh) => follow(under, fresh), noop)
            return settle(under, territory)
        } catch (err) {
            logWarn(target, 'account territory unavailable, using the page host', err)
            return settle(under, undefined)
        }
    })
    const confirmed = async (): Promise<MarketplaceInfo> => {
        const under = getAuth().status.value
        const marketplace = await memo.get()
        if (under !== AuthStatus.SIGNED_IN) return marketplace
        follow(under, await loadUserContext())
        return memo.get()
    }
    return { memo, confirmed, resolved: () => resolved, accountTerritory$ }
})

/**
 * Resolves the current marketplace, memoized per auth status: signed in, it is
 * the account's territory, from the week-old cache when there is one until the
 * session's network read lands; otherwise it is the page host's.
 */
export function getMarketplace(): Promise<MarketplaceInfo> {
    return marketplaceRef.value.memo.get()
}

/**
 * {@link getMarketplace} once the session's network read of the account has landed, never the
 * cached market alone: what a request that must not act in a territory the account left awaits.
 * Rejects when that read fails.
 */
export function confirmedMarketplace(): Promise<MarketplaceInfo> {
    return marketplaceRef.value.confirmed()
}

/**
 * The current territory for sync render and click paths: the resolved
 * marketplace's, else the page host's while the lookup this starts runs.
 * Async callers await {@link getMarketplace} instead.
 */
export function currentTerritory(): Territory {
    const resolved = marketplaceRef.value.resolved()
    if (resolved?.under === getAuth().status.value) return resolved.marketplace.musicTerritory
    getMarketplace().catch(errorHandler)
    return hostTerritory()
}

/**
 * Observable for the current marketplace, re-resolved on every settled auth
 * status. It holds its last value while auth is PENDING, so a signed-in account
 * never flashes the page host's territory first.
 */
export const marketplace$ = externalData<MarketplaceInfo | null>(null, (setData) => {
    let latest: Promise<MarketplaceInfo> | undefined
    return getAuth().status.onData((status) => {
        latest = undefined
        if (status === AuthStatus.PENDING) return
        const pending = getMarketplace()
        latest = pending
        pending
            .then((marketplace) => {
                if (latest === pending) setData(marketplace)
            })
            .catch(errorHandler)
    })
})

/**
 * The signed-in account's own music territory as the marketplace last resolved it (cached or
 * read); null signed out or while unknown, never the page host's.
 */
export const accountTerritory$: ObservableValue<Territory | null> = externalData<Territory | null>(
    null,
    (setData) => marketplaceRef.value.accountTerritory$.onData(setData)
)
